import { useEffect, useCallback, useMemo } from "react";
import { CssBaseline} from "@mui/material";
import ThemeProvider from "./theme/ThemeProvider";
import AppLoader from "shared-compoents/app-loader";
import AuthError from "shared-compoents/auth-error";
import router from "navigation";
import {
  InteractionStatus,
  InteractionType,
  InteractionRequiredAuthError,
  EventType,
} from "@azure/msal-browser";
import type { AuthenticationResult } from "@azure/msal-browser";
import {
  MsalAuthenticationTemplate,
  useMsal,
} from "@azure/msal-react";
import { useRoutes, useLocation } from "react-router-dom";
import { setUserData } from "api-store/user-data/slice";
import { loginRequest, AUTH_REDIRECT_ATTEMPT_KEY } from "authConfig";
import { useDispatch } from "react-redux";
import { PageProvider } from "contexts/PageContext";
import ScrollToTop from "shared-compoents/scroll-to-top";
import { useIdleTimer } from 'react-idle-timer' 
import { useLogout } from "shared-compoents/logout";
import MlopsAgent from "./pages/mlops-agent/mlopsAgent";

// Rendered only once the user is authenticated. Acquires the API token and
// renders the routed application.
function AuthenticatedApp() {
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useDispatch();
  const logout = useLogout();

  useIdleTimer({
    onIdle: () => {
      logout();
    },
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange',
      'focus'
    ],
    timeout: 1000 * 60 * 60,
    startOnMount: true,
    startManually: false,
    stopOnIdle: true,
    crossTab: true,
    name: 'idle-timer'
  });

  const acquireToken = useCallback(async () => {
    const account = instance.getActiveAccount() || accounts[0];
    if (!account) {
      return;
    }
    try {
      const token = await instance.acquireTokenSilent({
        scopes: ["openid", "profile", "email"],
        account,
      });
      if (token?.account) {
        dispatch(
          setUserData({
            user_name: token.account.username ?? "",
            name: token.account.name ?? "",
            access_token: token.accessToken,
          })
        );
      }
    } catch (error) {
      // Only fall back to an interactive redirect when no other interaction is
      // in progress, to avoid the interaction_in_progress error / redirect loop.
      if (
        error instanceof InteractionRequiredAuthError &&
        inProgress === InteractionStatus.None
      ) {
        await instance.acquireTokenRedirect({
          scopes: ["openid", "profile", "email"],
        });
      }
    }
  }, [accounts, dispatch, instance, inProgress]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      acquireToken();
    }
  }, [inProgress, acquireToken]);

  const content = useRoutes(router);
  const { pathname } = useLocation();
  // Home page has its own docked agent panel, so skip the floating widget there.
  const showFloatingAgent = pathname !== "/";

  return (
    <>
      <ScrollToTop />
      {content}
      {showFloatingAgent && <MlopsAgent />}
    </>
  );
}

function App() {
  const { instance } = useMsal();

  // Set the active account on login/token success so account state is
  // deterministic after a redirect returns.
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload
      ) {
        const account = (event.payload as AuthenticationResult).account;
        if (account) {
          instance.setActiveAccount(account);
        }
        // Successful auth clears the auth-error redirect guard.
        sessionStorage.removeItem(AUTH_REDIRECT_ATTEMPT_KEY);
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  const authRequest = useMemo(() => ({ scopes: loginRequest.scopes }), []);

  return (
    <PageProvider>
      <ThemeProvider>
        <CssBaseline />
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={authRequest}
          loadingComponent={AppLoader}
          errorComponent={AuthError}
        >
          <AuthenticatedApp />
        </MsalAuthenticationTemplate>
      </ThemeProvider>
    </PageProvider>
  );
}

export default App;
